// AdoptionAddendumPage — /send/adoption-addendum: send a Lease Adoption Addendum
// to add resident(s) to an existing tenancy WITHOUT removing anyone (WEL-131).
//
// Why this page exists: the Leaseholder Change agreement refuses to send unless
// at least one resident is leaving, so the plain "a roommate is moving in" case
// had no path at all. This is that path.
//
// APPLICATION-FIRST, the same step 1 as the leaseholder change (the situation is
// identical — an approved application against a unit that already has a lease):
//   • "Which application?" → picking one fills the entire working set.
//   • "On the lease today" — everyone currently on the lease. They all SIGN
//     (they're consenting to the addition), so each row just needs a good email.
//     No Continuing/Outgoing toggle: nobody is leaving.
//   • "Joining the lease" — the application's members, pre-included. Known minors
//     are shown excluded (they join as occupants, not leaseholders). "+ Add
//     person" is the manual escape hatch.
//   • "The lease being amended" — premises + BOTH lease dates, prefilled from the
//     tenancy and required: the addendum quotes them in its prose.
//
// All the rules live in lib/adoptionAddendumForm (pure + unit-tested); this file
// is presentation and wiring only.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  getAdoptionAddendumCandidates,
  sendAdoptionAddendum,
  adoptionAddendumErrorDetail,
  AdoptionAddendumNotEnabledError,
  type AdoptionAddendumCandidate,
  type SendAdoptionAddendumResult,
} from '@/lib/adoptionAddendumClient';
import {
  seedFromCandidate,
  includedJoining,
  validateAdoptionAddendumForm,
  canSendAdoptionAddendum,
  buildAdoptionAddendumBody,
  blankJoiningRow,
  isEmailInvalid,
  fullName,
  isYmd,
  joinNames,
  signerCount,
  type AdoptionAddendumFormState,
  type JoiningRow,
} from '@/lib/adoptionAddendumForm';

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Option label for a candidate: who's applying + where (+ when they applied).
 *  Never the group id when the names are missing — the address still identifies
 *  the application, an id identifies nothing (docs/no-ids-in-ui.md). */
function candidateOptionLabel(c: AdoptionAddendumCandidate): string {
  const names = joinNames(c.applicants) || 'Applicants not named';
  const address = c.propertyAddress || 'address unknown';
  const when = c.submittedAt ? ` (submitted ${fmtDate(c.submittedAt)})` : '';
  return `${names} — ${address}${when}`;
}

export function AdoptionAddendumPage(): React.JSX.Element {
  const [candidates, setCandidates] = useState<AdoptionAddendumCandidate[]>([]);
  const [candLoading, setCandLoading] = useState(true);
  const [candError, setCandError] = useState<string | null>(null);
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [form, setForm] = useState<AdoptionAddendumFormState | null>(null);
  const [sendEnabled, setSendEnabled] = useState(true);
  const [dormantDismissed, setDormantDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<{ message: string; detail: string[] } | null>(null);
  const [result, setResult] = useState<SendAdoptionAddendumResult | null>(null);

  // Load the candidates on mount. Each one is the complete working set, so
  // picking is instant — no second round-trip.
  useEffect(() => {
    let cancelled = false;
    getAdoptionAddendumCandidates()
      .then((res) => {
        if (cancelled) return;
        setCandidates(res.candidates);
        setSendEnabled(res.sendEnabled);
        setCandidatesLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setCandError(
          err instanceof ApiError
            ? ((err.body as { error?: string } | null)?.error ??
                `Could not load approved applications (HTTP ${err.status})`)
            : err instanceof Error
              ? err.message
              : 'Could not load approved applications',
        );
      })
      .finally(() => {
        if (!cancelled) setCandLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCandidate = candidates.find((c) => c.groupId === selectedGroupId) ?? null;

  const handlePick = (groupId: string): void => {
    setSelectedGroupId(groupId);
    setSendError(null);
    const c = candidates.find((x) => x.groupId === groupId);
    setForm(c ? seedFromCandidate(c) : null);
  };

  const patch = (next: Partial<AdoptionAddendumFormState>): void => {
    setForm((prev) => (prev ? { ...prev, ...next } : prev));
  };

  const patchOnLease = (key: number, email: string): void => {
    setForm((prev) =>
      prev
        ? { ...prev, onLease: prev.onLease.map((r) => (r.key === key ? { ...r, email } : r)) }
        : prev,
    );
  };

  const patchJoining = (key: number, next: Partial<JoiningRow>): void => {
    setForm((prev) =>
      prev
        ? { ...prev, joining: prev.joining.map((r) => (r.key === key ? { ...r, ...next } : r)) }
        : prev,
    );
  };

  const problems = form ? validateAdoptionAddendumForm(form) : [];
  const ready = !!form && canSendAdoptionAddendum(form);
  const willEmail = form ? signerCount(form) : 0;

  const handleSend = async (): Promise<void> => {
    if (!form || !activeCandidate || !ready) return;
    setBusy(true);
    setSendError(null);
    try {
      const res = await sendAdoptionAddendum(
        activeCandidate.groupId,
        buildAdoptionAddendumBody(form),
      );
      setResult(res);
      const n = willEmail;
      toast.success(`Sent the addendum — ${n} signing ${n === 1 ? 'link' : 'links'} emailed.`, {
        title: 'Adoption addendum',
      });
    } catch (err) {
      if (err instanceof AdoptionAddendumNotEnabledError) {
        setSendError({ message: err.message, detail: [] });
      } else {
        setSendError(
          adoptionAddendumErrorDetail(err) ?? {
            message: err instanceof Error ? err.message : 'Could not send the addendum',
            detail: [],
          },
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // --- Sent: the roster + where to track it. The form is done. ---
  if (result) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 'var(--space-7)' }}>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Adoption addendum
          </h1>
        </div>
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Addendum sent</h3>
          </div>
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {result.adultResidentCount} signing{' '}
              {result.adultResidentCount === 1 ? 'link' : 'links'} emailed. Everyone named on the
              addendum signs in parallel; Welcome Home Properties countersigns once they all have.
            </p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.submitters.map((s) => (
                    <tr key={s.role}>
                      <td>{s.role}</td>
                      <td>{s.name ?? '—'}</td>
                      <td>{s.email ?? '—'}</td>
                      <td>
                        <span
                          className="pill"
                          data-tone={
                            s.status === 'sent' || s.status === 'completed' ? 'success' : 'neutral'
                          }
                        >
                          {s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Track it on the <Link to="/requests">Requests page</Link>.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Adoption addendum
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Add residents to an existing lease when nobody is leaving. Pick the approved
          application and its members are added to the tenancy; everyone already on the lease stays
          on it and signs to consent. Welcome Home Properties countersigns last.{' '}
          <Link to="/send/leaseholder-change">Use a leaseholder change</Link> instead when someone
          is also moving out.
        </p>
      </div>

      {candidatesLoaded && !sendEnabled && !dormantDismissed && (
        <div
          className="alert"
          data-tone="info"
          role="note"
          style={{ marginBottom: 'var(--space-5)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}
        >
          <div className="alert-body" style={{ flex: 1 }}>
            <div className="alert-title">Sends are dormant</div>
            Addendum sends are off (<code>DOCUSEAL_SEND_ENABLED</code>) — the send will be refused.
          </div>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            data-icon-only
            aria-label="Dismiss dormant notice"
            onClick={() => setDormantDismissed(true)}
          >
            ×
          </button>
        </div>
      )}

      <div className="stack">
        {/* Step 1 — which application? */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Which application?</h3>
          </div>
          <div className="stack-sm">
            {candError && (
              <div className="alert" data-tone="danger" role="alert">
                <div className="alert-body">{candError}</div>
              </div>
            )}
            {candLoading ? (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                Loading approved applications…
              </p>
            ) : candidates.length === 0 && !candError ? (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                No approved applications are associated with an existing tenancy right now.
              </p>
            ) : (
              <div className="field">
                <label htmlFor="aa-candidate">Approved application</label>
                <select
                  id="aa-candidate"
                  className="input"
                  value={selectedGroupId}
                  onChange={(e) => handlePick(e.target.value)}
                >
                  <option value="">Select an application…</option>
                  {candidates.map((c) => (
                    <option key={c.groupId} value={c.groupId}>
                      {candidateOptionLabel(c)}
                    </option>
                  ))}
                </select>
                <span className="helper">
                  Approved applications whose unit already has residents on a lease. The property,
                  current residents, and lease dates below all come from the application.
                </span>
              </div>
            )}
          </div>
        </section>

        {/* On the lease today — everyone signs; nobody is leaving. */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">On the lease today</h3>
          </div>
          {!form ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick an application to load the current residents.
            </p>
          ) : form.onLease.length === 0 ? (
            <div className="alert" data-tone="warning" role="note">
              <div className="alert-body">
                No current residents are on file for this unit — an addendum amends an existing
                lease, so there is nothing to add to here.
              </div>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
                Everyone here stays on the lease and signs the addendum to consent to the addition.
                Each needs a valid email.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {form.onLease.map((r, idx) => {
                  const name = fullName(r.firstName, r.lastName) || `Resident ${idx + 1}`;
                  const invalid = isEmailInvalid(r.email);
                  return (
                    <div
                      key={r.key}
                      style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}
                    >
                      <div style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
                        <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>
                          {name}
                        </span>
                        {r.alsoOnApplication && (
                          <span className="pill" data-tone="info" style={{ marginLeft: 'var(--space-2)' }}>
                            Also on this application
                          </span>
                        )}
                      </div>
                      <div className="field" style={{ flex: 1, minWidth: 0, margin: 0 }}>
                        <input
                          type="email"
                          className="input"
                          value={r.email}
                          onChange={(e) => patchOnLease(r.key, e.target.value)}
                          placeholder="resident@example.com"
                          aria-label={`${name} email`}
                          aria-invalid={invalid || undefined}
                        />
                        {invalid && (
                          <span className="helper" data-state="error">
                            Check this address.
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Joining the lease. */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Joining the lease</h3>
          </div>
          {!form ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick an application to load who is being added.
            </p>
          ) : (
            <>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
                Every adult on the application is included. Each signs the addendum and takes on the
                lease as written.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {form.joining.map((r, idx) => {
                  const label = fullName(r.firstName, r.lastName) || `Person ${idx + 1}`;
                  const invalid = isEmailInvalid(r.email);
                  return (
                    <div
                      key={r.key}
                      style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {r.source === 'application' ? (
                          <div style={{ paddingTop: 6 }}>
                            <span
                              style={{
                                fontWeight: 'var(--weight-medium)',
                                color: r.minor ? 'var(--fg-muted)' : 'var(--fg)',
                              }}
                            >
                              {label}
                            </span>
                            {r.minor && (
                              <span className="pill" data-tone="neutral" style={{ marginLeft: 'var(--space-2)' }}>
                                Minor — not added
                              </span>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <input
                              className="input"
                              value={r.firstName}
                              onChange={(e) => patchJoining(r.key, { firstName: e.target.value })}
                              placeholder="First name"
                              aria-label={`Person ${idx + 1} first name`}
                            />
                            <input
                              className="input"
                              value={r.lastName}
                              onChange={(e) => patchJoining(r.key, { lastName: e.target.value })}
                              placeholder="Last name"
                              aria-label={`Person ${idx + 1} last name`}
                            />
                          </div>
                        )}
                      </div>
                      <div className="field" style={{ flex: 1, minWidth: 0, margin: 0 }}>
                        <input
                          type="email"
                          className="input"
                          value={r.email}
                          onChange={(e) => patchJoining(r.key, { email: e.target.value })}
                          placeholder={r.minor ? 'Not needed — joins as an occupant' : 'joining@example.com'}
                          aria-label={`${label} email`}
                          aria-invalid={invalid || undefined}
                          disabled={r.minor}
                        />
                        {invalid && (
                          <span className="helper" data-state="error">
                            Check this address.
                          </span>
                        )}
                      </div>
                      {r.source === 'manual' && (
                        <button
                          type="button"
                          className="btn"
                          data-variant="ghost"
                          data-size="sm"
                          data-icon-only
                          aria-label={`Remove ${label}`}
                          onClick={() =>
                            patch({ joining: form.joining.filter((x) => x.key !== r.key) })
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => patch({ joining: [...form.joining, blankJoiningRow()] })}
                style={{ alignSelf: 'flex-start', marginTop: 'var(--space-3)' }}
              >
                + Add person
              </button>
            </>
          )}
        </section>

        {/* The lease being amended — both dates are quoted in the document. */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">The lease being amended</h3>
          </div>
          {!form ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick an application to load the lease details.
            </p>
          ) : (
            <div className="stack-sm">
              <div className="field">
                <label htmlFor="aa-premises">Premises</label>
                <input
                  id="aa-premises"
                  className="input"
                  value={form.premises}
                  onChange={(e) => patch({ premises: e.target.value })}
                  placeholder="123 Vine St, Walla Walla, WA 99362"
                  aria-invalid={!form.premises.trim() || undefined}
                  aria-describedby="aa-premises-hint"
                />
                <span
                  id="aa-premises-hint"
                  className="helper"
                  data-state={!form.premises.trim() ? 'error' : undefined}
                >
                  {form.premises.trim()
                    ? 'As it should read on the addendum.'
                    : 'Required — the addendum names the premises.'}
                </span>
              </div>
              {/* Both bounds are quoted verbatim in the document's prose, so each
                  reports its own required state rather than only feeding the panel
                  below — and the helper stops asserting "from the tenancy" the
                  moment the field is cleared, which would otherwise be a lie. */}
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="aa-start">Lease dated</label>
                  <input
                    id="aa-start"
                    type="date"
                    className="input"
                    value={form.leaseStartDate}
                    onChange={(e) => patch({ leaseStartDate: e.target.value })}
                    aria-invalid={!isYmd(form.leaseStartDate) || undefined}
                    aria-describedby="aa-start-hint"
                  />
                  <span
                    id="aa-start-hint"
                    className="helper"
                    data-state={!isYmd(form.leaseStartDate) ? 'error' : undefined}
                  >
                    {!isYmd(form.leaseStartDate)
                      ? 'Required — the addendum quotes the date the lease was signed.'
                      : activeCandidate?.leaseStartDate
                        ? 'From the tenancy — confirm it matches the signed lease.'
                        : 'Not on file — enter the date from the signed lease.'}
                  </span>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="aa-end">Term ends</label>
                  <input
                    id="aa-end"
                    type="date"
                    className="input"
                    value={form.leaseEndDate}
                    onChange={(e) => patch({ leaseEndDate: e.target.value })}
                    aria-invalid={!isYmd(form.leaseEndDate) || undefined}
                    aria-describedby="aa-end-hint"
                  />
                  <span
                    id="aa-end-hint"
                    className="helper"
                    data-state={!isYmd(form.leaseEndDate) ? 'error' : undefined}
                  >
                    {!isYmd(form.leaseEndDate)
                      ? 'Required — the addendum quotes the date the term ends.'
                      : activeCandidate?.leaseEndDate
                        ? 'From the tenancy — the addendum leaves the term unchanged.'
                        : 'Not on file — enter the current term’s end date.'}
                  </span>
                </div>
              </div>
              <div className="field">
                <label htmlFor="aa-terms">Additional terms (optional)</label>
                <textarea
                  id="aa-terms"
                  className="input"
                  rows={2}
                  value={form.otherTerms}
                  onChange={(e) => patch({ otherTerms: e.target.value })}
                  placeholder="Anything else this addendum settles"
                />
                <span className="helper">
                  Added under the document’s “Any additional terms are defined below:” line. Leave
                  blank and the line does not appear.
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Send */}
        {form && (
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Send</h3>
            </div>
            <div className="stack-sm">
              {problems.length > 0 && (
                <div className="alert" data-tone="warning" role="note">
                  <div className="alert-body">
                    <div className="alert-title">Before this can go out</div>
                    <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                      {problems.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {sendError && (
                <div className="alert" data-tone="danger" role="alert">
                  <div className="alert-body">
                    <div className="alert-title">{sendError.message}</div>
                    {sendError.detail.length > 0 && (
                      <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                        {sendError.detail.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {ready && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                  Adds {joinNames(includedJoining(form.joining))} to the lease at{' '}
                  {form.premises.trim()}. {willEmail} signing{' '}
                  {willEmail === 1 ? 'link goes' : 'links go'} out now; Welcome Home Properties
                  countersigns once everyone has signed.
                </p>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => void handleSend()}
                disabled={!ready || busy}
                style={{ alignSelf: 'flex-start' }}
              >
                {busy ? 'Sending…' : 'Send addendum'}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
